import React, { useRef, useState } from "react"
import { useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faExclamationTriangle,
	faMapMarkerAlt,
	faSync,
} from "@fortawesome/free-solid-svg-icons"
import { URL } from "../../../store/actions/depannageActions"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"

// Coordonnées d'un parc / fourrière / parc police (GE-47, P10). Le serveur
// les calcule (Nominatim) à l'enregistrement quand le point est créé ou que
// son adresse change ; un lien Google Maps collé ici les CORRIGE et prime
// (extra_data.coords_source = "maps"). Le mobile s'en sert pour proposer
// « Tu es au Parc X ? » à ≤ 300 m.

export const coordsPoint = (adresse) => {
	const extra = (adresse || {}).extra_data || {}
	return extra.latitude && extra.longitude
		? {
				lat: extra.latitude,
				lng: extra.longitude,
				source: extra.coords_source,
			}
		: null
}

export const lienCarte = (coords) =>
	`https://www.google.com/maps?q=${coords.lat},${coords.lng}`

// Lien « voir sur la carte » ou badge « sans coordonnées » (liste + modal).
export const CoordonneesResume = ({ adresse }) => {
	const coords = coordsPoint(adresse)
	if (!coords)
		return (
			<span style={st.sansCoords}>
				<FontAwesomeIcon icon={faExclamationTriangle} />
				Sans coordonnées
			</span>
		)
	return (
		<a
			href={lienCarte(coords)}
			target="_blank"
			rel="noopener noreferrer"
			style={st.lien}
			onClick={(e) => e.stopPropagation()}
		>
			<FontAwesomeIcon icon={faMapMarkerAlt} />
			Voir sur la carte
		</a>
	)
}

// `onModifierAdresse(fn)` : mise à jour FONCTIONNELLE de l'adresse du
// formulaire — une résolution qui arrive après une saisie d'adresse ne
// réécrit pas un instantané périmé (revue 02.10, R14).
const CoordonneesPointV2 = ({ adresse, onModifierAdresse }) => {
	const { token } = useSelector((state) => state.navInfo.login)
	const [lien, setLien] = useState("")
	const [resolution, setResolution] = useState(null)
	const resolutionId = useRef(0)
	// Dernier texte résolu : la sortie du champ après un collage ne relance
	// pas la même résolution.
	const dernierResolu = useRef("")
	const coords = coordsPoint(adresse)

	const poser = (lat, lng) =>
		onModifierAdresse((courante) => ({
			...courante,
			extra_data: {
				...((courante || {}).extra_data || {}),
				latitude: lat,
				longitude: lng,
				coords_source: "maps",
			},
		}))

	// Même résolution que le sélecteur de lieu (PointInteretSelectV2) :
	// lien maps court ou complet via api/resolve-maps-link, ou « lat, lng ».
	// Lancée au COLLAGE ou à la validation (Entrée / sortie du champ), plus
	// à chaque frappe (R14).
	const resoudre = (texte) => {
		setResolution(null)
		const valeur = (texte || "").trim()
		if (!valeur || valeur === dernierResolu.current) return
		dernierResolu.current = valeur
		const brut = valeur.match(/^(-?\d{1,3}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)$/)
		if (brut) {
			poser(brut[1], brut[2])
			return
		}
		if (!/^https?:\/\//.test(valeur)) {
			dernierResolu.current = ""
			setResolution({
				statut: "erreur",
				texte: "Coller un lien Google Maps ou « lat, lng ».",
			})
			return
		}
		const id = ++resolutionId.current
		setResolution({ statut: "loading" })
		fetch(`${URL}api/resolve-maps-link?url=${encodeURIComponent(valeur)}`, {
			headers: { Authorization: `Bearer ${token}` },
		})
			.then(async (res) => {
				const json = await res.json().catch(() => ({}))
				if (!res.ok)
					throw new Error(json.message || "Lien non reconnu.")
				if (id !== resolutionId.current) return
				poser(json.latitude, json.longitude)
				setResolution(null)
			})
			.catch((err) => {
				if (id !== resolutionId.current) return
				dernierResolu.current = "" // réessai possible
				setResolution({
					statut: "erreur",
					texte: err.message || "Lien non reconnu.",
				})
			})
	}

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			<div style={st.ligne}>
				{coords ? (
					<span style={{ color: COLORS.text }}>
						{coords.lat}, {coords.lng}
						<span style={{ color: COLORS.textMuted }}>
							{coords.source === "maps"
								? " · lien Google Maps"
								: " · calculées depuis l'adresse"}
						</span>
					</span>
				) : (
					<span style={{ color: COLORS.textMuted }}>
						Calculées automatiquement à l'enregistrement.
					</span>
				)}
				<CoordonneesResume adresse={adresse} />
			</div>
			<input
				type="text"
				style={gs.input}
				value={lien}
				onChange={(e) => {
					setLien(e.target.value)
					setResolution(null)
				}}
				onPaste={(e) => {
					const colle = e.clipboardData.getData("text")
					if (colle) {
						e.preventDefault()
						setLien(colle)
						resoudre(colle)
					}
				}}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						e.preventDefault()
						resoudre(lien)
					}
				}}
				onBlur={() => resoudre(lien)}
				placeholder="Corriger : coller un lien Google Maps (maps.app.goo.gl/…)"
			/>
			{resolution && resolution.statut === "loading" && (
				<span style={{ fontSize: 12, color: COLORS.textMuted }}>
					<FontAwesomeIcon icon={faSync} spin /> Résolution du lien…
				</span>
			)}
			{resolution && resolution.statut === "erreur" && (
				<span style={{ fontSize: 12, color: COLORS.danger }}>
					{resolution.texte}
				</span>
			)}
		</div>
	)
}

const st = {
	ligne: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 12,
		fontSize: 13,
		flexWrap: "wrap",
	},
	lien: {
		display: "inline-flex",
		alignItems: "center",
		gap: 6,
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.accent,
		textDecoration: "none",
	},
	sansCoords: {
		display: "inline-flex",
		alignItems: "center",
		gap: 6,
		padding: "2px 10px",
		borderRadius: 99,
		fontSize: 11,
		fontWeight: 700,
		color: COLORS.warning,
		backgroundColor: COLORS.warningLight,
	},
}

export default CoordonneesPointV2
